/**
 * Le document lui-même — chunk 9.AI.1.
 *
 * Ce composant **rend** une projection ; il n'en calcule aucune. Les statuts, les
 * familles, les colonnes et le contexte de processus arrivent déjà décidés par
 * `projectReport()` côté serveur. Reconstruire ne serait-ce qu'une règle ici
 * permettrait à l'écran de dire autre chose que le rapport sur les mêmes données,
 * ce que tout le chunk existe pour empêcher.
 *
 * Deux audiences, un seul rendu : la différence est dans ce que la projection
 * CONTIENT, pas dans deux arbres React parallèles qui finiraient par diverger.
 * Ce qui n'est pas dans la charge client n'a aucun chemin pour s'afficher.
 *
 * C'est aussi la surface qui s'imprime. `print:` porte la mise en page papier ;
 * l'export PDF est l'impression du navigateur, pas un moteur serveur.
 */

import { comparisonFieldLabel, type Comparison } from "@kollectivia/shared";
import { useTranslation } from "react-i18next";

import {
  formatComparisonValue,
  toComparisonLocale,
  type ComparisonLocale,
} from "./comparisonFormat";

type Projection = Comparison.ReportProjection;
type Cell = Comparison.ReportCell;

/**
 * Un statut lisible → sa couleur et son libellé.
 *
 * `not_offered` et `unknown` ne partagent NI l'un NI l'autre : le premier dit que
 * l'assureur ne l'offre pas, le second qu'on ne l'a pas trouvé. Les rendre
 * identiques transformerait une lacune de lecture en refus de couverture.
 */
const TON_STATUT: Record<Cell["readable"], string> = {
  offered: "text-slate-900",
  not_offered: "text-slate-500",
  // 9.AM.1 — NEUTRE. « La proposition ne le dit pas » n'est pas un reproche à
  // l'assureur : le teinter comme un manquement transformerait un silence en
  // jugement, dans un document remis au client.
  not_provided: "text-slate-500",
  unknown: "text-amber-700",
  to_verify: "text-amber-700",
  unreadable: "text-red-700",
};

function CelluleRapport({
  cell,
  fieldKey,
  locale,
}: {
  cell: Cell;
  fieldKey: string;
  locale: ComparisonLocale;
}) {
  const { t } = useTranslation(undefined, { lng: locale === "en-CA" ? "en" : "fr" });

  // Une valeur ne s'affiche que sur un statut qui en porte une. Sur les autres, le
  // libellé EST l'information — « non offert » n'est pas un vide.
  if (cell.readable !== "offered") {
    return (
      <span className={`text-sm ${TON_STATUT[cell.readable]}`}>
        {t(`report.status.${cell.readable}`)}
      </span>
    );
  }

  const formatte = formatComparisonValue(cell.value, fieldKey, cell.unit, locale);
  return (
    <span className="text-sm tabular-nums text-slate-900">
      {formatte.display}
      {!formatte.suppressUnit && cell.unit ? (
        <span className="ml-1 text-xs text-slate-500">{cell.unit}</span>
      ) : null}
    </span>
  );
}

/** Regroupe les lignes par famille de garantie, dans l'ordre d'arrivée. */
function grouperParFamille(rows: Projection["rows"]) {
  const groupes = new Map<string, Projection["rows"]>();
  for (const row of rows) {
    const cle = row.guarantee_family ?? "__autres__";
    const liste = groupes.get(cle) ?? [];
    liste.push(row);
    groupes.set(cle, liste);
  }
  return [...groupes.entries()];
}

export function ReportDocument({
  projection,
  language,
}: {
  projection: Projection;
  language: string;
}) {
  const { t } = useTranslation(undefined, { lng: language });
  const locale = toComparisonLocale(language);
  // 9.AU.1 — le dictionnaire partagé est borné à fr/en ; `language` est la langue
  // du rapport, déjà choisie en amont.
  const langue = language.toLowerCase().startsWith("en") ? "en" : "fr";
  const groupes = grouperParFamille(projection.rows);
  const pourConseiller = projection.audience === "advisor";

  // La clé d'une cellule diffère selon l'audience — identifiant pour le
  // conseiller, code d'assureur pour le client. La projection l'a déjà décidé ;
  // on se contente de la lire de la même façon qu'elle l'a écrite.
  const cleColonne = (colonne: Projection["columns"][number]) =>
    colonne.submission_id ?? colonne.insurer_code;

  if (projection.columns.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
        <p className="text-sm text-slate-600">{t("report.document.empty")}</p>
      </div>
    );
  }

  return (
    <article
      lang={langue}
      className="rounded-xl border border-slate-200 bg-white p-4 lg:p-6 print:rounded-none print:border-0 print:p-0"
    >
      <p className="mb-3 text-sm text-slate-600">{t("report.document.source_language")}</p>
      <div className="overflow-x-auto print:overflow-visible">
        <table className="w-full border-collapse text-left">
          <caption className="sr-only">{t("report.document.table_caption")}</caption>
          <thead>
            <tr className="border-b border-slate-300">
              <th
                scope="col"
                className="py-2 pr-4 align-bottom text-xs font-semibold uppercase tracking-wide text-slate-500"
              >
                {t("report.document.column_guarantee")}
              </th>
              {projection.columns.map((colonne) => (
                <th
                  key={cleColonne(colonne)}
                  scope="col"
                  className="min-w-[9rem] py-2 pr-4 align-bottom text-sm font-semibold text-slate-900"
                >
                  <span translate="no">{colonne.insurer_name}</span>
                  {/* L'état d'extraction n'est PAS une information de client. */}
                  {pourConseiller && colonne.reception_state ? (
                    <span className="mt-0.5 block text-xs font-normal text-slate-500">
                      {t(`reception.state.${colonne.reception_state}`, {
                        defaultValue: colonne.reception_state,
                      })}
                    </span>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>

          {groupes.map(([famille, lignes]) => (
            <tbody key={famille} className="print:break-inside-avoid">
              <tr>
                <th
                  scope="colgroup"
                  colSpan={projection.columns.length + 1}
                  className="pb-1 pt-5 text-xs font-semibold uppercase tracking-wide text-slate-600"
                >
                  {famille === "__autres__"
                    ? t("report.document.family_other")
                    : comparisonFieldLabel(famille, langue)}
                </th>
              </tr>
              {lignes.map((ligne) => (
                <tr key={ligne.field_key} className="border-b border-slate-100 align-top">
                  <th
                    scope="row"
                    className="max-w-[18rem] break-words py-2 pr-4 text-sm font-normal text-slate-700"
                  >
                    {comparisonFieldLabel(ligne.field_key, langue)}
                  </th>
                  {projection.columns.map((colonne) => {
                    const cell = ligne.cells[cleColonne(colonne)];
                    return (
                      <td key={cleColonne(colonne)} className="py-2 pr-4">
                        {cell ? (
                          <>
                            <CelluleRapport
                              cell={cell}
                              fieldKey={ligne.field_key}
                              locale={locale}
                            />
                            {/* Visible dans les DEUX versions. Un client a le droit
                                de savoir qu'une ligne n'est pas encore vérifiée. */}
                            {cell.needs_review && cell.readable !== "to_verify" ? (
                              <span className="mt-0.5 block text-xs font-medium text-amber-700">
                                {t("report.status.to_verify")}
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <span className="text-sm text-slate-300">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>

      {/* Les assureurs sollicités SANS proposition. Jamais une colonne : une
          colonne vide se lirait « aucune garantie offerte », et rien dans le
          document ne dirait que cette lecture est fausse. */}
      {projection.process_context.length > 0 ? (
        <section className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-3 print:break-inside-avoid">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {t("report.process.title")}
          </h3>
          <ul className="mt-2 space-y-1">
            {projection.process_context.map((entree) => (
              <li key={entree.insurer_code} className="text-sm text-slate-700">
                <span translate="no">{entree.insurer_code}</span>
                {" — "}
                {t(`report.process.state.${entree.readable}`)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </article>
  );
}
